import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { examService } from '../../../services/examService';
import { StudentExam } from '../../../types/examination';
import PaperInput from '../marks/PaperInput';
import { PaperDraft, draftOf, draftProblem, entryOf, sameDraft } from '../marks/paperStatus';

/**
 * Correct one student's papers in one exam, from their results. The same
 * rules as Enter Marks apply on the server; submitted papers stay
 * read-only until unlocked.
 */
const EditExamMarksDialog: React.FC<{
  studentId: string;
  studentName: string;
  exam: StudentExam | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ studentId, studentName, exam, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [drafts, setDrafts] = useState<PaperDraft[]>([]);
  const [refused, setRefused] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDrafts(exam ? exam.papers.map(draftOf) : []);
    setRefused({});
  }, [exam]);

  const changed = useMemo(
    () => (exam ? exam.papers.flatMap((p, i) => (!p.isLocked && drafts[i] && !sameDraft(drafts[i], draftOf(p)) ? [i] : [])) : []),
    [exam, drafts],
  );
  const invalid = changed.some(i => draftProblem(drafts[i], exam!.papers[i].maxMarks));

  const save = async () => {
    if (!exam) return;
    setSaving(true);
    try {
      const outcome = await examService.recordStudentMarks(studentId, exam.examId, changed.map(i => ({ subjectId: exam.papers[i].subjectId, ...entryOf(drafts[i]), previous: entryOf(draftOf(exam.papers[i])) })));
      if (outcome.refused.length) {
        setRefused(Object.fromEntries(outcome.refused.map(r => [r.subjectId, r.reason])));
        enqueueSnackbar(`${outcome.saved} saved, ${outcome.refused.length} not — see below`, { variant: 'warning' });
        onSaved();
        return;
      }
      enqueueSnackbar(`${exam.examName} updated for ${studentName}`, { variant: 'success' });
      onSaved();
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not save the marks', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!exam} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>{exam?.examName}</Typography>
        <Typography variant="body2" color="text.secondary">{studentName}</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 1 }}>
        {exam?.papers.map((p, i) => (
          <Box key={p.subjectId} sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <Typography variant="body2" fontWeight={600} sx={{ width: { sm: 150 }, flexShrink: 0 }}>
                {p.subjectName}
                {p.isLocked && <LockOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'text.disabled' }} />}
              </Typography>
              {drafts[i] && (
                <PaperInput
                  draft={drafts[i]}
                  maxMarks={p.maxMarks}
                  locked={p.isLocked}
                  label={`${studentName}, ${p.subjectName}`}
                  error={!!refused[p.subjectId]}
                  onChange={next => setDrafts(prev => prev.map((d, k) => (k === i ? next : d)))}
                />
              )}
            </Stack>
            {refused[p.subjectId] && <Typography variant="caption" color="error">Not saved — {refused[p.subjectId]}</Typography>}
          </Box>
        ))}
        {exam?.papers.some(p => p.isLocked) && (
          <Alert severity="info" icon={<LockOutlined fontSize="small" />} sx={{ mt: 1.5 }}>Submitted papers can't be changed until they're unlocked.</Alert>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={saving || !changed.length || invalid}>
          {saving ? 'Saving…' : changed.length ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}` : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExamMarksDialog;
